import * as React from 'react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { useApproveDirectPO, useCreatePO, useSubmitPO } from '@/hooks/usePOMutations'
import type { components } from '@/lib/api/generated'
import { createPOSchema, describeItemsError, type POFormValues } from '@/lib/schemas/po'
import { useUIStore } from '@/store/uiStore'
import type { POLineItemPrefill } from '@/store/uiStore'
import { signalKind } from '@/hooks/useDashboard'
import { POWizard, usePOTotals, type WizardStep } from './POWizard'

const EMPTY: POFormValues = {
  vendor: '',
  supplier: '',
  location_to_receive: '',
  notes: '',
  items: [],
  expected_delivery: '',
  vendor_po_ref: '',
  approval_notes: '',
}

// Map the wider form values to the backend's CreatePORequest, dropping
// fields the API doesn't accept here (vendor_po_ref isn't accepted yet;
// approval_notes travels as the `{ notes }` body on submit/approve instead
// — see schema for rationale).
//
// Session 7.5 — a PO has one recipient. If the user picked a supplier we
// send that and let the backend derive `purchase_orders.vendor` from the
// first item. If the user picked a vendor directly, we send the vendor
// explicitly and leave supplier empty. Each line item still carries its own
// `vendor` so reporting attributes units to the right brand either way.
function toCreateRequest(values: POFormValues): components['schemas']['CreatePORequest'] {
  const supplier = (values.supplier ?? '').trim()
  const vendor = (values.vendor ?? '').trim()
  const expected = (values.expected_delivery ?? '').trim()
  const location = (values.location_to_receive ?? '').trim()
  // Fail loud instead of silently saving a draft with no receive location.
  // Zod should have already enforced this; this is a belt-and-suspenders
  // check after the recurring Save-Draft-loses-location report.
  if (!location) {
    throw new Error('Please pick a receive location before saving.')
  }
  return {
    supplier: supplier || undefined,
    vendor: !supplier && vendor ? vendor : undefined,
    location_to_receive: location,
    notes: values.notes,
    // Send the draft-time estimate so it round-trips on reopen. Empty
    // string is sent as undefined to leave the column NULL on create.
    expected_delivery: expected || undefined,
    items: values.items.map((it) => ({
      variant_id: it.variant_id,
      product_id: it.product_id,
      product_title: it.product_title,
      variant_title: it.variant_title,
      sku: it.sku,
      barcode: it.barcode,
      vendor: it.vendor,
      retail_price: it.retail_price,
      cost_base: it.cost_base,
      qty_ordered: it.qty_ordered,
      notes: it.notes,
    })),
  }
}

function toPOLineItem(p: POLineItemPrefill): POFormValues['items'][number] {
  return {
    variant_id: p.variant_id,
    product_id: p.product_id,
    product_title: p.product_title,
    variant_title: p.variant_title ?? '',
    sku: p.sku ?? '',
    barcode: p.barcode ?? '',
    vendor: p.vendor,
    retail_price: 0,
    cost_base: p.unit_cost ?? 0,
    qty_ordered: p.recommended_qty || 1,
    notes: '',
    on_hand: p.on_hand ?? 0,
    incoming: p.incoming ?? 0,
    rec_qty: p.recommended_qty ?? 0,
    signal: signalKind(p.action_signal),
    return_rate: p.return_rate,
  }
}

export function CreatePOModal() {
  const activeModal = useUIStore((s) => s.activeModal)
  const prefillItems = useUIStore((s) => s.prefillItems)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const open = activeModal === 'create'

  const [step, setStep] = useState<WizardStep>(1)
  const form = useForm<POFormValues>({
    resolver: zodResolver(createPOSchema),
    defaultValues: EMPTY,
    mode: 'onChange',
  })

  const createMutation = useCreatePO()
  const submitMutation = useSubmitPO()
  const approveDirectMutation = useApproveDirectPO()
  const busy =
    createMutation.isPending ||
    submitMutation.isPending ||
    approveDirectMutation.isPending

  useEffect(() => {
    if (open) {
      if (prefillItems && prefillItems.length > 0) {
        // Pre-seed vendor (if all selected rows share one vendor)
        const vendors = [...new Set(prefillItems.map((p) => p.vendor).filter(Boolean))]
        const singleVendor = vendors.length === 1 ? vendors[0] : ''
        form.reset({
          ...EMPTY,
          vendor: singleVendor,
          items: prefillItems.map(toPOLineItem),
        })
      } else {
        form.reset(EMPTY)
      }
      setStep(1)
      createMutation.reset()
      submitMutation.reset()
      approveDirectMutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function closeModal() {
    setActiveModal(null)
  }

  async function goNext() {
    if (step === 1) {
      // The at-least-one refine in createPOSchema attaches its error to
      // `vendor`, so triggering vendor surfaces the recipient-missing error.
      const ok = await form.trigger(['vendor', 'supplier', 'location_to_receive'])
      if (ok) setStep(2)
      return
    }
    if (step === 2) {
      const ok = await form.trigger('items')
      if (ok) {
        setStep(3)
      } else {
        const msg = describeItemsError(form.formState.errors.items, form.getValues('items'))
        toast.error(msg ?? 'Some line items are missing required data.')
      }
      return
    }
  }

  function goBack() {
    if (step === 3) setStep(2)
    else if (step === 2) setStep(1)
  }

  async function onSaveDraft() {
    const ok = await form.trigger()
    if (!ok) {
      // Surface the first failing step
      const errs = form.formState.errors
      if (errs.vendor || errs.supplier || errs.location_to_receive) setStep(1)
      else if (errs.items) setStep(2)
      return
    }
    const values = form.getValues()
    try {
      const po = await createMutation.mutateAsync(toCreateRequest(values))
      toast.success(`Draft saved: ${po.po_number || po.po_id}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save draft')
    }
  }

  async function onSubmitForApproval() {
    const ok = await form.trigger()
    if (!ok) {
      const errs = form.formState.errors
      if (errs.vendor || errs.supplier || errs.location_to_receive) setStep(1)
      else if (errs.items) setStep(2)
      return
    }
    const values = form.getValues()
    try {
      const po = await createMutation.mutateAsync(toCreateRequest(values))
      await submitMutation.mutateAsync({
        po_id: po.po_id,
        notes: (values.approval_notes ?? '').trim() || undefined,
      })
      toast.success(`Submitted for approval: ${po.po_number || po.po_id}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit PO')
    }
  }

  // Skip-approval fast path: create the draft, then approve it directly
  // (DRAFT → APPROVED) so the buyer never touches the AWAITING_APPROVAL queue.
  async function onApproveNow() {
    const ok = await form.trigger()
    if (!ok) {
      const errs = form.formState.errors
      if (errs.vendor || errs.supplier || errs.location_to_receive) setStep(1)
      else if (errs.items) setStep(2)
      return
    }
    const values = form.getValues()
    try {
      const po = await createMutation.mutateAsync(toCreateRequest(values))
      await approveDirectMutation.mutateAsync({
        po_id: po.po_id,
        notes: (values.approval_notes ?? '').trim() || undefined,
      })
      toast.success(`Approved: ${po.po_number || po.po_id}`)
      closeModal()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve PO')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeModal()}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex h-[85vh] w-[85vw] max-w-none flex-col gap-0 overflow-hidden p-0 text-[15px] sm:max-w-none"
        showCloseButton={false}
      >
        <DialogHeader className="border-b border-[var(--line-warm)] px-5 py-3.5 sm:text-left">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-base font-semibold">
              New purchase order
            </DialogTitle>
            <span className="text-xs text-[var(--mute)]">
              Step {step} of 3
            </span>
            {prefillItems && prefillItems.length > 0 && (
              <span className="text-xs text-[var(--brand)] font-medium">
                · {prefillItems.length} item{prefillItems.length === 1 ? '' : 's'} pre-loaded from dashboard
              </span>
            )}
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={closeModal}
              aria-label="Close"
              data-action="close"
            >
              ✕
            </Button>
          </div>
          <DialogDescription className="sr-only">
            Multi-step wizard to create a new purchase order.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex flex-1 flex-col overflow-hidden"
            onSubmit={(e) => e.preventDefault()}
            noValidate
          >
            <POWizard
              step={step}
              disabled={busy}
              // Free navigation between steps. Forward Save still runs the
              // full Zod validation and bounces the user back to the first
              // failing step, so jumping ahead before filling step 1 is a
              // preview, not a way to bypass required fields.
              onStepClick={(n) => setStep(n)}
            />
            <WizardFooter
              step={step}
              busy={busy}
              busyLabel={
                approveDirectMutation.isPending
                  ? 'Approving…'
                  : createMutation.isPending && !submitMutation.isPending
                    ? 'Saving…'
                    : submitMutation.isPending
                      ? 'Submitting…'
                      : null
              }
              onCancel={closeModal}
              onBack={goBack}
              onNext={goNext}
              onSaveDraft={onSaveDraft}
              onSubmitApproval={onSubmitForApproval}
              onApproveNow={onApproveNow}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

// ────────────────────────────────────────────────────────────────────
// Per-step footer — exported so EditPOModal can reuse the same UX.
// Footer layout follows chat1.md:145:
//   `Save draft` (left) · `MOV ✓ met` · `← Back` · `Next →`
// ────────────────────────────────────────────────────────────────────
export function WizardFooter({
  step,
  busy,
  busyLabel,
  onCancel,
  onBack,
  onNext,
  onSaveDraft,
  onSubmitApproval,
  onApproveNow,
  saveLabel = 'Save draft',
  submitLabel = 'Submit for approval',
  approveNowLabel = 'Approve now',
  hideSubmit = false,
  hideSaveDraft = false,
  leftPrefix,
}: {
  step: WizardStep
  busy: boolean
  busyLabel: string | null
  onCancel: () => void
  onBack: () => void
  onNext: () => void
  onSaveDraft: () => void
  onSubmitApproval?: () => void
  // Skip-approval fast path (DRAFT → APPROVED). When provided, a secondary
  // step-3 button lets the buyer self-approve in one click instead of routing
  // through the AWAITING_APPROVAL queue. Omitted on non-DRAFT surfaces.
  onApproveNow?: () => void
  saveLabel?: string
  // Label for the step-3 primary action. EditPOModal passes 'Approve' for
  // AWAITING_APPROVAL POs and 'Mark Ordered' for APPROVED so the button text
  // matches the next state-machine transition instead of always reading
  // 'Submit for approval'.
  submitLabel?: string
  approveNowLabel?: string
  hideSubmit?: boolean
  hideSaveDraft?: boolean
  leftPrefix?: React.ReactNode
}) {
  const totals = usePOTotals()
  const movLabel =
    totals.mov === 0
      ? null
      : totals.movMet
        ? `MOV ${formatShort(totals.mov)} ✓ met`
        : `MOV ${formatShort(totals.mov)} — below by ${formatShort(
            totals.mov - totals.subtotal
          )}`

  return (
    <div className="flex items-center gap-2 border-t border-[var(--line-warm)] bg-[var(--surface-card)] px-5 py-3">
      {leftPrefix}
      {!hideSaveDraft && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSaveDraft}
          disabled={busy}
          data-action="save-draft"
        >
          {busyLabel ?? saveLabel}
        </Button>
      )}
      <div className="flex-1" />
      {movLabel && (
        <span
          className={`text-xs ${
            totals.movMet ? 'text-[var(--mute)]' : 'text-[var(--status-awaiting-fg)]'
          }`}
        >
          {movLabel}
        </span>
      )}
      {step === 1 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </Button>
      )}
      {step > 1 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onBack}
          disabled={busy}
          data-action="back"
        >
          ← {step === 2 ? 'Setup' : 'Items'}
        </Button>
      )}
      {step < 3 && (
        <Button
          type="button"
          size="sm"
          onClick={onNext}
          disabled={busy}
          data-action="next"
        >
          {step === 1 ? 'Items →' : 'Review →'}
        </Button>
      )}
      {step === 3 && onApproveNow && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onApproveNow}
          disabled={busy}
          data-action="approve-now"
        >
          {approveNowLabel}
        </Button>
      )}
      {step === 3 && !hideSubmit && onSubmitApproval && (
        <Button
          type="button"
          size="sm"
          onClick={onSubmitApproval}
          disabled={busy}
          data-action="submit-approval"
        >
          {submitLabel}
        </Button>
      )}
    </div>
  )
}

function formatShort(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}
